import axios from 'axios';
import { API_BASE_URL } from '../config';

/**
 * Dedicated API service for MMS Mobile to prevent interference with main app authentication
 */
let mmsApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const setupMmsInterceptors = () => {
  // Request interceptor for MMS Mobile
  mmsApi.interceptors.request.use(config => {
    // Only add token for non-login endpoints
    if (!config.url.includes('/login') && !config.url.includes('/verify_otp')) {
      const mmsToken = localStorage.getItem('mms_mobile_token');
      if (mmsToken) {
        config.headers.Authorization = `Bearer ${mmsToken}`;
      }
    }
    
    // URLs starting with '/api/mms/mobile/' are already correct
    // Only modify relative URLs that don't start with '/api/'
    if (config.url && !config.url.startsWith('/api/') && !config.url.startsWith('http')) {
      config.url = '/api/mms/mobile/' + config.url;
    }
    
    console.log('🔧 MMS API Request:', {
      url: config.url,
      method: config.method,
      hasAuth: !!config.headers.Authorization,
      data: config.data
    });
    
    return config;
  });

  // Response interceptor for MMS Mobile
  mmsApi.interceptors.response.use(
    response => {
      console.log('📥 MMS API Response:', {
        url: response.config.url,
        status: response.status,
        dataStatus: response.data?.status,
        responseData: response.data
      });
      return response;
    },
    error => {
      console.error('❌ MMS API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        requestData: error.config?.data,
        responseData: error.response?.data
      });
      
      // Don't automatically redirect on 401 - let components handle it
      // This prevents interference with main app authentication
      return Promise.reject(error);
    }
  );
};

setupMmsInterceptors();

// Helper function to reinitialize MMS API
export const reinitializeMmsApi = () => {
  mmsApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  setupMmsInterceptors();
};

// MMS Mobile specific API methods
export const mmsLogin = async (phone, password) => {
  const response = await mmsApi.post('/api/mms/mobile/login', { phone, password });
  return response;
};

export const mmsVerifyOtp = async (otp, otpToken) => {
  const response = await mmsApi.post('/api/mms/mobile/verify_otp', { otp, otp_token: otpToken });
  return response;
};

export const mmsValidateSession = async () => {
  const response = await mmsApi.post('/api/mms/mobile/validate_session');
  return response;
};

export const mmsGetJobs = async (statusFilter = null) => {
  const params = {};
  if (statusFilter && statusFilter !== 'all') {
    params.status = statusFilter;
  }
  const response = await mmsApi.get('/api/mms/mobile/jobs', { params });
  return response;
};

export const mmsUpdateLocation = async (locationData) => {
  const response = await mmsApi.post('/api/mms/mobile/location', locationData);
  return response;
};

export const mmsLogout = async () => {
  const response = await mmsApi.post('/api/mms/mobile/logout');
  return response;
};

// ============================================
// PAUSE/RESUME WORKFLOW METHODS
// ============================================

/**
 * Get all available pause reasons
 */
export const mmsPauseReasons = async () => {
  const response = await mmsApi.get('/api/mms/pause-reasons');
  return response;
};

/**
 * Pause a job with a specified reason
 */
export const mmsJobPause = async (assignmentId, pauseReasonId, pauseNotes = null) => {
  const response = await mmsApi.post('/api/mms/assignments/pause', {
    assignment_id: assignmentId,
    pause_reason_id: pauseReasonId,
    pause_notes: pauseNotes
  });
  return response;
};

/**
 * Resume a paused job (optionally with image)
 */
export const mmsJobResume = async (assignmentId, resumeNotes = null, imageFile = null) => {
  const formData = new FormData();
  formData.append('assignment_id', assignmentId);
  if (resumeNotes) {
    formData.append('resume_notes', resumeNotes);
  }
  if (imageFile) {
    formData.append('image', imageFile);
  }
  
  const response = await axios.post(
    `${API_BASE_URL}/api/mms/assignments/resume`,
    formData,
    {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('mms_mobile_token')}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response;
};

/**
 * Get paused jobs for current worker or filtered by worker_id (admin only)
 */
export const mmsPausedJobs = async (workerId = null, reasonId = null) => {
  const params = {};
  if (workerId) {
    params.worker_id = workerId;
  }
  if (reasonId) {
    params.reason_id = reasonId;
  }
  const response = await mmsApi.get('/api/mms/paused-jobs', { params });
  return response;
};

export default mmsApi;